import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'active', label: 'Attiva' },
  { value: 'completed', label: 'Completata' },
  { value: 'archived', label: 'Archiviata' },
  { value: 'cancelled', label: 'Annullata' },
];

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const formatDate = (ts) => {
  if (!ts) return '-';
  try { return format(new Date(ts), 'dd/MM/yyyy HH:mm'); } catch { return ts; }
};

// Row component — navigates to detail page on click
const RequestRow = ({ req, onRowClick }) => {
  const hasAccepted = (req?.accepted_quotes_count ?? 0) > 0;
  const quotesCount = req?.quotes_count ?? 0;

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer"
      onClick={() => onRowClick(req?.id)}
    >
      {/* Client */}
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <div className="font-bold text-gray-900 max-w-[200px] truncate">{req?.client_email || '-'}</div>
        {req?.client_full_name && (
          <div className="text-gray-500 text-xs">({req?.client_full_name})</div>
        )}
      </td>
      {/* Title */}
      <td className="px-6 py-4 text-sm text-gray-900 max-w-[180px] truncate">
        {req?.title || '-'}
      </td>
      {/* Service type */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
        {req?.service_type || '-'}
      </td>
      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE?.[req?.status] || 'bg-gray-100 text-gray-700'}`}>
          {req?.status || '-'}
        </span>
      </td>
      {/* Urgency */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
        {req?.urgency || '-'}
      </td>
      {/* Created at */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
        {formatDate(req?.created_at)}
      </td>
      {/* Quotes count */}
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${hasAccepted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
          {quotesCount} preventiv{quotesCount === 1 ? 'o' : 'i'}
        </span>
      </td>
      {/* Viewed by */}
      <td className="px-6 py-4 whitespace-nowrap text-sm min-w-[120px]">
        {Array.isArray(req?.viewed_by) && req?.viewed_by?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {req?.viewed_by?.map((username) => {
              let pillStyle;
              if (username === 'pederzollisimone') {
                pillStyle = { backgroundColor: '#32b3e5', color: '#ffffff' };
              } else if (username === 'salem') {
                pillStyle = null; // use tailwind classes
              } else {
                pillStyle = null;
              }
              const pillClass = `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mr-1 ${
                username === 'salem' ?'bg-purple-100 text-purple-700'
                  : username !== 'pederzollisimone' ?'bg-gray-100 text-gray-600' :''
              }`;
              return (
                <span
                  key={username}
                  className={pillClass}
                  style={username === 'pederzollisimone' ? pillStyle : undefined}
                >
                  {username}
                </span>
              );
            })}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
};

const AdminRichiestePage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchRequests = async (page, status, search) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        ?.from('admin_requests_view')
        ?.select('*', { count: 'exact' })
        ?.order('created_at', { ascending: false })
        ?.range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (status) {
        query = query?.eq('status', status);
      }

      if (search?.trim()) {
        query = query?.or(
          `client_email.ilike.%${search?.trim()}%,title.ilike.%${search?.trim()}%,client_full_name.ilike.%${search?.trim()}%`
        );
      }

      const { data, count, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      setRequests(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(currentPage, statusFilter, searchQuery);
  }, [currentPage, statusFilter, searchQuery]);

  const handleStatusChange = (e) => {
    setStatusFilter(e?.target?.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowClick = (requestId) => {
    navigate(`/admin/richieste/${requestId}`);
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Richieste</h1>
          <p className="text-sm text-gray-500 mt-1">Tutte le richieste di servizio dei clienti — clicca su una riga per vedere i dettagli</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={statusFilter}
            onChange={handleStatusChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {STATUS_OPTIONS?.map((opt) => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Cerca per email, nome o titolo..."
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[240px]"
          />
          <span className="text-sm text-gray-500">{totalCount} risultati</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Servizio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Urgenza</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data creazione</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preventivi ricevuti</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visto da</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3">Caricamento...</span>
                    </div>
                  </td>
                </tr>
              ) : requests?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    Nessuna richiesta trovata
                  </td>
                </tr>
              ) : (
                requests?.map((req) => (
                  <RequestRow key={req?.id} req={req} onRowClick={handleRowClick} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                Risultati <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
                <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, totalCount)}</span> di{' '}
                <span className="font-medium">{totalCount}</span>
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Precedente
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Successiva
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminRichiestePage;
